import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
/**
 * @typedef {Object} VariantPrice
 * @property {string} price - The formatted price.
 * @property {string} compareAtPrice - The formatted compare at price.
 * @property {boolean} onSale - Whether the variant is discounted.
 */

class ProductPrice extends Component {
  /** @type {Record<string, VariantPrice> | null | undefined} */
  #variantPrices;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  updatedCallback() {
    super.updatedCallback();

    // A combined listing can swap in a different product, and with it a different set of prices.
    this.#variantPrices = undefined;
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    this.#showSelectedPrice(event);

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };

  /**
   * Updates the price from the prices rendered with the page, so the new price is shown
   * on the next frame instead of after the variant change request returns.
   *
   * The response handler above still runs and remains authoritative.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #showSelectedPrice(event) {
    const prices = this.#getVariantPrices();
    if (!prices) return;

    const variantId = this.#getSelectedVariantId(event);
    // Missing for combinations that have no variant, and for combined listings, where the
    // selection resolves to a product whose prices were not rendered with this page.
    const selectedPrice = variantId ? prices[variantId] : undefined;
    if (!selectedPrice) return;

    const priceContainer = this.querySelector('[ref="priceContainer"]');
    const regularPrice = priceContainer?.querySelector('.price__regular');
    const salePrice = priceContainer?.querySelector('.price__sale');
    if (!priceContainer || !regularPrice || !salePrice) return;

    for (const element of priceContainer.querySelectorAll('.price')) {
      element.innerHTML = selectedPrice.price;
    }

    for (const element of priceContainer.querySelectorAll('.compare-at-price')) {
      element.innerHTML = selectedPrice.compareAtPrice;
    }

    regularPrice.classList.toggle('price__hidden', selectedPrice.onSale);
    salePrice.classList.toggle('price__hidden', !selectedPrice.onSale);
  }

  /**
   * Resolves the variant the shopper just selected.
   *
   * The option element carries the variant it resolves to when combined with the other
   * current selections, which is the same value the variant picker uses to update the URL.
   *
   * @param {ProductSelectEvent} event - The product select event.
   * @returns {string | undefined} The variant ID.
   */
  #getSelectedVariantId(event) {
    const optionValueId = event.detail?.optionValueId;
    if (!optionValueId || !(event.target instanceof Element)) return;

    const selectedOption = event.target.querySelector(`[data-option-value-id="${CSS.escape(optionValueId)}"]`);
    if (!(selectedOption instanceof HTMLElement)) return;

    return selectedOption.dataset.variantId;
  }

  /**
   * @returns {Record<string, VariantPrice> | null} The prices of each variant, if they were rendered.
   */
  #getVariantPrices() {
    if (this.#variantPrices !== undefined) return this.#variantPrices;

    const data = this.querySelector('script[data-variant-prices]')?.textContent;

    try {
      this.#variantPrices = data ? JSON.parse(data) : null;
    } catch (error) {
      console.warn('[product-price] Could not parse variant prices:', error);
      this.#variantPrices = null;
    }

    return this.#variantPrices;
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
